import { type ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { InlineInput } from "@/components/inline-input";
import { wacToken } from "@/libraries/design/foundations";
import { messages } from "@/templates/paid-landing/sections/cfind-4666-frictionless-first-look/messages";

const containerStyle = css({
    fontFamily: wacToken("font.charlie.display"),
    fontSize: "24px",
    lineHeight: "40px",
    letterSpacing: "0.3px",
    color: wacToken("color.neutral.light1000"),
    minHeight: "5rem"
});

const inlineInputStyle = css({
    "color": wacToken("color.primary.blue"),
    "borderBlockEnd": "3px solid transparent",
    "borderImage": `linear-gradient(to right, ${wacToken(
        "color.primary.purple"
    )} 0, ${wacToken(
        "color.primary.purple"
    )} calc(25% - 5px), #ffa903 calc(25% + 5px), #ffa903 calc(50% - 5px), #82b535 calc(50% + 5px), #82b535 calc(75% - 5px), ${wacToken(
        "color.primary.blue"
    )} calc(75% + 5px), ${wacToken("color.primary.blue")} 100% ) 1`,
    "textAlign": "center",
    "minWidth": "100px",
    "display": "inline",
    "minHeight": "34px",
    "lineHeight": "31px",
    "verticalAlign": "text-top",
    "::before": {
        content: " ",
        whiteSpace: "pre"
    },
    "::after": {
        content: " ",
        whiteSpace: "pre"
    }
});

const MadLibLabel = ({
    id,
    str,
    forSelector
}: {
    id?: string;
    str: ReactNode;
    forSelector: string;
}) => {
    const focusInput = () => {
        const element = window.document.querySelector(
            forSelector
        ) as HTMLSpanElement;
        element?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            focusInput();
        }
    };

    return (
        <span
            id={id}
            onClick={focusInput}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="button"
        >
            {str}
        </span>
    );
};

export interface PaidJiraAiMadLibProps {
    workingOnSuggestion: string;
    goalSuggestion: string;
    workingOnValue: string;
    goalValue: string;
    onChange?: (change: { workingOnValue: string; goalValue: string }) => void;
}

export const PaidJiraAiMadLib = ({
    workingOnSuggestion,
    goalSuggestion,
    workingOnValue,
    goalValue,
    onChange
}: PaidJiraAiMadLibProps) => {
    return (
        <div css={containerStyle}>
            <FormattedMessage
                {...messages.madLib}
                values={{
                    workingOn: (str: ReactNode) => (
                        <MadLibLabel
                            id="workingOnLabel"
                            str={str}
                            forSelector="#working-on-input span[contenteditable]"
                        />
                    ),
                    goal: (str: ReactNode) => (
                        <MadLibLabel
                            id="goalLabel"
                            str={str}
                            forSelector="#goal-input span[contenteditable]"
                        />
                    ),
                    workingOnInput: (
                        <span css={inlineInputStyle} id="working-on-input">
                            <InlineInput
                                value={workingOnValue}
                                placeholder={workingOnSuggestion}
                                ariaLabelledBy="workingOnLabel"
                                animatePlaceholder
                                onChange={(value) => {
                                    if (onChange) {
                                        onChange({
                                            workingOnValue: value,
                                            goalValue
                                        });
                                    }
                                }}
                            />
                        </span>
                    ),
                    goalInput: (
                        <span css={inlineInputStyle} id="goal-input">
                            <InlineInput
                                value={goalValue}
                                placeholder={goalSuggestion}
                                ariaLabelledBy="goalLabel"
                                animatePlaceholder
                                onChange={(value) => {
                                    if (onChange) {
                                        onChange({
                                            workingOnValue,
                                            goalValue: value
                                        });
                                    }
                                }}
                            />
                        </span>
                    )
                }}
            />
        </div>
    );
};
